import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import flowerLogo from '../../assets/gymea-flower-only.png';
import './WelcomeModal.css';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDashboardClick = () => {
    onClose();
    // The user is already on the dashboard, so we just close the modal
  };

  const modalContent = (
    <div 
      className="welcome-modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div className="welcome-modal-container">
        <div className="welcome-modal-header">
          <button
            className="welcome-modal-close"
            onClick={onClose}
            type="button"
            aria-label="Close welcome modal"
          >
            ×
          </button>
        </div>
        
        <div className="welcome-modal-content">
          <div className="welcome-modal-visual">
            <div className="clean-flower-visual">
              <img 
                src={flowerLogo} 
                alt="Gymea Lily Flower" 
                className="clean-flower-image"
              />
            </div>
          </div>
          
          <div className="welcome-modal-text">
            <h1 className="welcome-modal-title">Welcome, early adopter!</h1>
            
            <div className="welcome-modal-message">
              <p>
                We're so excited you're here - you must really have your finger on the pulse. 
                We're just getting started and we're a small team, but we really hope that 
                Gymea Edu will help make your life heaps easier.
              </p>
              
              <p>
                If you notice any glitches - or if you'd like to send in a Teacher 
                Testimonial to feature! - you can contact us{' '}
                <a 
                  href="https://docs.google.com/forms/d/e/1FAIpQLSeb137Z79HtvYCeb59ppvQq8q9q76_AjHmd4tbi-GlUPAIGnw/viewform?usp=sharing&ouid=117310775465521436495" 
                  className="welcome-modal-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                , or from the link you'll always find at the footer of the page.
              </p>
              
              <p>
                Thank you for coming on this adventure with us. For the love of teaching, 
                we really hope you like it!
              </p>
              
              <p className="welcome-modal-signature">
                - The Gymea Edu Team
              </p>
            </div>

            <button
              className="welcome-modal-button"
              onClick={handleDashboardClick}
              type="button"
            >
              Take me to the dashboard
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render the modal at the document body level
  return createPortal(modalContent, document.body);
}; 